import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { FormInputField } from '@/components/forms/form-input-field';
import { FormTextareaField } from '@/components/forms/form-textarea-field';
import { Loader2 } from 'lucide-react';

import { adContentSchema, type AdContentFormValues } from './ad-content-schema';

interface AdContentFormProps {
  initialValues?: Partial<AdContentFormValues>;
  onSubmit: (values: AdContentFormValues) => Promise<void>;
  isSubmitting?: boolean;
  onCancel: () => void;
  isEditing?: boolean;
}

export function AdContentForm({
  initialValues,
  onSubmit,
  isSubmitting,
  onCancel,
  isEditing,
}: AdContentFormProps) {
  const form = useForm<AdContentFormValues>({
    resolver: zodResolver(adContentSchema),
    defaultValues: {
      title: initialValues?.title || '',
      notes: initialValues?.notes || '',
      redirectUrl: initialValues?.redirectUrl || '',
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col">
        <div className="grid gap-6 px-6 py-4">
          <FormInputField
            control={form.control}
            name="title"
            label="Title"
            placeholder="Advertisement title"
            required
          />
          <FormTextareaField
            control={form.control}
            name="notes"
            label="Notes (Optional)"
            placeholder="Any internal notes or description"
          />
          <FormInputField
            control={form.control}
            name="redirectUrl"
            label="Redirect URL"
            placeholder="https://..."
            type="url"
            required
          />
        </div>
        <div className="flex flex-col-reverse justify-end gap-3 border-t border-border px-6 py-6 md:flex-row">
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            className="w-full text-muted-foreground hover:bg-muted hover:text-foreground md:w-auto"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-primary px-8 text-white shadow-lg shadow-primary/20 hover:bg-primary/90 md:w-auto"
          >
            {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {isEditing ? 'Save Ad' : 'Create Ad'}
          </Button>
        </div>
      </form>
    </Form>
  );
}
